import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import { FullPageLoader, FullPageMessage } from '@/components/FullPage';

import { useAuth } from './context';
import type { LoginRedirectState } from './redirect';

/**
 * Admits only a confirmed admin session (`/api/admin/me/` loaded). Anonymous visitors go to
 * `/login`, remembering the requested page; a blocked or expired session adds `?blocked=1` /
 * `?expired=1` so the login screen can explain what happened.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, endReason, refreshMe } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (status === 'loading') return <FullPageLoader />;

  if (status === 'error') {
    return (
      <FullPageMessage
        title={t('page.connectionTitle')}
        text={t('page.connectionText')}
        actionLabel={t('actions.retry')}
        onAction={() => void refreshMe()}
      />
    );
  }

  if (status === 'anonymous') {
    const search = endReason === 'blocked' ? '?blocked=1' : endReason === 'expired' ? '?expired=1' : '';
    const state: LoginRedirectState = {
      from: { pathname: location.pathname, search: location.search, hash: location.hash },
    };
    return <Navigate to={`/login${search}`} replace state={state} />;
  }

  return children;
}
